// OutsourcePrintWorkTicket.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import JsBarcode from 'jsbarcode';
import axios from 'axios';
import DOMPurify from 'dompurify';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { toast } from 'react-toastify';
import './OutsourcePrintWorkTicket.css';

// Map hex codes to color names
const colorMap = {
  '#FFA500': 'ORANGE',
  '#FF0000': 'RED',
  '#00FF00': 'GREEN',
  '#0000FF': 'BLUE',
  '#FFFFFF': 'WHITE',
  '#000000': 'BLACK',
};

const getColorName = (hexCode) =>
  colorMap[hexCode?.toUpperCase()] || hexCode || 'N/A';

// Barcode Component - Fixed for print with proper positioning
const BarcodeComponent = ({ value }) => {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && value) {
      try {
        barcodeRef.current.innerHTML = '';

        const svg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );
        svg.setAttribute('id', `barcode-${value}`);
        svg.setAttribute('class', 'barcode-svg');
        barcodeRef.current.appendChild(svg);

        JsBarcode(svg, value, {
          format: 'CODE128',
          height: 40,
          width: 1.5,
          displayValue: false,
          background: '#ffffff',
          lineColor: '#000000',
          margin: 5,
          fontSize: 0,
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [value]);

  return (
    <div
      ref={barcodeRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        padding: '5px',
        minWidth: '150px',
        minHeight: '50px',
        position: 'relative',
        top: '0',
        right: '0',
      }}
      className="barcode-container"
    />
  );
};

// STL Preview Component - FIXED for print
const StlPreview = ({ url }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: false,
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(100, 100);
    renderer.setClearColor(0x000000);
    renderer.setPixelRatio(window.devicePixelRatio);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
    directionalLight.position.set(0, 0, 1);
    scene.add(directionalLight);

    const sideLight = new THREE.DirectionalLight(0xffffff, 0.5);
    sideLight.position.set(1, 0, 0);
    scene.add(sideLight);

    let mesh = null;
    let animationFrame = null;

    const loader = new STLLoader();
    loader.load(
      url,
      (geometry) => {
        const material = new THREE.MeshPhongMaterial({
          color: 0xcccccc,
          shininess: 10,
          specular: 0x111111,
        });
        mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        const box = new THREE.Box3().setFromObject(mesh);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        mesh.position.sub(center);
        camera.position.z = maxDim * 1.5;
        camera.lookAt(new THREE.Vector3(0, 0, 0));

        const render = () => {
          renderer.render(scene, camera);
        };

        render();

        const animate = () => {
          render();
          animationFrame = requestAnimationFrame(animate);
        };
        animate();
      },
      undefined,
      (error) => {
        console.error('Error loading STL:', error);
      }
    );

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      renderer.dispose();
    };
  }, [url]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100px',
        height: '100px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        position: 'relative',
      }}
      className="stl-container"
    >
      <canvas
        ref={canvasRef}
        width={100}
        height={100}
        style={{
          width: '100px',
          height: '100px',
          display: 'block',
        }}
      />
    </div>
  );
};

// File Preview Component - FIXED for print
const FilePreview = ({ file }) => {
  const fileExtension = file.name?.toLowerCase().split('.').pop() || '';
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];
  const stlExtensions = ['stl'];
  const pdfExtensions = ['pdf'];

  if (imageExtensions.includes(fileExtension)) {
    return (
      <img
        src={file.url}
        alt={file.name}
        crossOrigin="anonymous"
        loading="eager"
        style={{
          width: '100px',
          height: '100px',
          objectFit: 'cover',
          display: 'block',
          border: '1px solid #ddd',
          borderRadius: '4px',
        }}
        onError={(e) => {
          e.target.style.display = 'none';
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = `
            width: 100px;
            height: 100px;
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center; 
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #f9f9f9;
            color: #666;
            font-size: 10px;
          `;
          errorDiv.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span style="margin-top: 3px;">Error</span>
          `;
          e.target.parentNode.replaceChild(errorDiv, e.target);
        }}
      />
    );
  }

  if (stlExtensions.includes(fileExtension)) {
    return <StlPreview url={file.url} />;
  }

  if (pdfExtensions.includes(fileExtension)) {
    return (
      <div
        style={{
          width: '100px',
          height: '100px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#fff',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#e74c3c"
          strokeWidth="2"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        <span
          style={{
            fontSize: '8px',
            color: '#e74c3c',
            marginTop: '3px',
            fontWeight: 'bold',
          }}
        >
          PDF
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100px',
        height: '100px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ddd',
        borderRadius: '4px',
        backgroundColor: '#f8f9fa',
      }}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#495057"
        strokeWidth="2"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
      </svg>
      <span
        style={{
          fontSize: '8px',
          color: '#495057',
          marginTop: '3px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
        }}
      >
        {fileExtension || 'FILE'}
      </span>
    </div>
  );
};

// Helper function to get user info from localStorage
const getUserInfo = () => {
  const userLabId =
    localStorage.getItem('userLabId') || localStorage.getItem('labId') || '';
  const userName =
    localStorage.getItem('userName') ||
    localStorage.getItem('username') ||
    'Unknown';
  const labName =
    localStorage.getItem('labName') ||
    localStorage.getItem('currentLabName') ||
    '';
  const userId = localStorage.getItem('userId') || '';
  const token =
    localStorage.getItem('accessToken') || localStorage.getItem('token') || '';

  return { userLabId, userName, labName, userId, token };
};

// Helper function to get file URL
const getFileUrl = (filePath) => {
  if (!filePath) return '';
  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';
  return `${API_BASE_URL}/uploads/${filePath}`;
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

// Helper function to format date only
const formatDateOnly = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Main OutsourcePrintWorkTicket component
const OutsourcePrintWorkTicket = () => {
  const { caseNumber } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notesHistory, setNotesHistory] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [sourceLabName, setSourceLabName] = useState('N/A');
  const [destinationLabName, setDestinationLabName] = useState('N/A');
  const [originalFiles, setOriginalFiles] = useState([]);
  const [additionalFiles, setAdditionalFiles] = useState([]);

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // Fetch outsource case details
  useEffect(() => {
    const fetchOutsourceCaseDetails = async () => {
      try {
        setLoading(true);
        const { token, userLabId } = getUserInfo();

        const response = await axios.get(
          `${API_BASE_URL}/api/outsource-case/case-number/${caseNumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              userLabId: userLabId,
              'x-user-lab-id': userLabId,
            },
            withCredentials: true,
          }
        );

        if (response.data && response.data.success) {
          const fetchedCaseData = response.data.data;
          setCaseData(fetchedCaseData);

          setSourceLabName(fetchedCaseData.sourceLab?.labName || 'N/A');
          setDestinationLabName(
            fetchedCaseData.destinationLab?.labName || 'N/A'
          );

          setOriginalFiles(fetchedCaseData.selectedFiles || []);
          setAdditionalFiles(fetchedCaseData.uploadedFiles || []);

          if (fetchedCaseData._id) {
            fetchNotes(fetchedCaseData._id, fetchedCaseData);
          }
        } else {
          setError('Failed to fetch outsource case details');
          toast.error('Failed to load case details');
        }
      } catch (err) {
        console.error('Error fetching outsource case:', err);
        setError(
          'Error fetching case details: ' +
            (err.response?.data?.message || err.message)
        );
        toast.error('Failed to load case details');
      } finally {
        setLoading(false);
      }
    };

    if (caseNumber) {
      fetchOutsourceCaseDetails();
    }
  }, [caseNumber]);

  // Fetch notes for outsource case
  const fetchNotes = async (caseId, fetchedCaseData) => {
    if (!caseId) return;

    setLoadingNotes(true);
    try {
      const { token, userLabId, userId } = getUserInfo();

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/${caseId}/notes`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            userLabId: userLabId,
            'x-user-lab-id': userLabId,
            'x-user-id': userId,
          },
          withCredentials: true,
        }
      );

      if (response.data && response.data.success) {
        let apiNotes = response.data.data.notesHistory || [];

        let initialNotes = [];

        if (
          fetchedCaseData?.notes &&
          typeof fetchedCaseData.notes === 'string' &&
          fetchedCaseData.notes.trim()
        ) {
          initialNotes.push({
            noteId: `initial-note-${Date.now()}`,
            _id: `initial-${Date.now()}`,
            content: fetchedCaseData.notes,
            createdAt: fetchedCaseData.createdAt || new Date().toISOString(),
            addedBy: {
              labName: fetchedCaseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source',
              userName: fetchedCaseData.createdBy?.name || 'System',
              userLabId: fetchedCaseData.sourceLab?.userLabId,
            },
          });
        }

        if (
          fetchedCaseData?.notesHistory &&
          Array.isArray(fetchedCaseData.notesHistory) &&
          fetchedCaseData.notesHistory.length > 0
        ) {
          const formattedInitialNotes = fetchedCaseData.notesHistory.map(
            (note) => ({
              ...note,
              noteId:
                note.noteId || note._id || `initial-${Date.now()}-${Math.random()}`,
              _id: note._id || `initial-${Date.now()}-${Math.random()}`,
              addedBy: note.addedBy || {
                labName: fetchedCaseData.sourceLab?.labName || 'Source Lab',
                labRole: 'source',
                userName: fetchedCaseData.createdBy?.name || 'System',
              },
            })
          );
          initialNotes = [...initialNotes, ...formattedInitialNotes];
        }

        const allNotes = [...initialNotes, ...apiNotes];

        const uniqueNotes = [];
        const seen = new Set();

        for (const note of allNotes) {
          const key = `${note.content}-${new Date(note.createdAt).toISOString()}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueNotes.push(note);
          }
        }

        uniqueNotes.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setNotesHistory(uniqueNotes);
      } else {
        let initialNotes = [];
        if (
          fetchedCaseData?.notes &&
          typeof fetchedCaseData.notes === 'string' &&
          fetchedCaseData.notes.trim()
        ) {
          initialNotes.push({
            noteId: `initial-note-${Date.now()}`,
            _id: `initial-${Date.now()}`,
            content: fetchedCaseData.notes,
            createdAt: fetchedCaseData.createdAt || new Date().toISOString(),
            addedBy: {
              labName: fetchedCaseData.sourceLab?.labName || 'Source Lab',
              labRole: 'source',
              userName: fetchedCaseData.createdBy?.name || 'System',
            },
          });
        }
        setNotesHistory(initialNotes);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      let initialNotes = [];
      if (
        fetchedCaseData?.notes &&
        typeof fetchedCaseData.notes === 'string' &&
        fetchedCaseData.notes.trim()
      ) {
        initialNotes.push({
          noteId: `initial-note-${Date.now()}`,
          _id: `initial-${Date.now()}`,
          content: fetchedCaseData.notes,
          createdAt: fetchedCaseData.createdAt || new Date().toISOString(),
          addedBy: {
            labName: fetchedCaseData.sourceLab?.labName || 'Source Lab',
            labRole: 'source',
            userName: fetchedCaseData.createdBy?.name || 'System',
          },
        });
      }
      setNotesHistory(initialNotes);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Strip HTML for safe rendering
  const stripHtml = (html) =>
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'div', 'ul', 'li'],
    }) || 'N/A';

  // Function to convert teeth array/list to range format
  const formatTeethRange = (teethString) => {
    if (!teethString || teethString.trim() === '') return '';

    const teethArray = teethString
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');
    if (teethArray.length === 0) return '';

    const numbers = teethArray
      .map((t) => parseInt(t))
      .filter((n) => !isNaN(n));
    if (numbers.length === 0) return teethString;

    numbers.sort((a, b) => a - b);

    const ranges = [];
    let start = numbers[0];
    let end = numbers[0];

    for (let i = 1; i < numbers.length; i++) {
      if (numbers[i] === end + 1) {
        end = numbers[i];
      } else {
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(start + '-' + end);
        }
        start = numbers[i];
        end = numbers[i];
      }
    }

    if (start === end) {
      ranges.push(start.toString());
    } else {
      ranges.push(start + '-' + end);
    }

    return ranges.join(', ');
  };

  const getFormattedTeeth = (procedure) => {
    if (!procedure.teeth) return '';
    let teethString = '';
    if (Array.isArray(procedure.teeth)) {
      teethString = procedure.teeth.join(', ');
    } else if (typeof procedure.teeth === 'string') {
      teethString = procedure.teeth;
    } else {
      return '';
    }
    return formatTeethRange(teethString);
  };

  // ============================================================
  // PRINT HANDLER
  // Same approach as simple PrintWorkTicket (which works on server)
  // Waits for images to load before calling window.print()
  // ============================================================
  const handlePrint = () => {
    setTimeout(() => {
      const images = Array.from(document.querySelectorAll('img'));
      const imagePromises = images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      Promise.all(imagePromises).then(() => {
        setTimeout(() => {
          window.print();
        }, 300);
      });
    }, 100);
  };

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading outsource case details...</p>
      </div>
    );

  if (error)
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <p>{error}</p>
        <Link to="/outsource-received" className="btn btn-secondary">
          Back
        </Link>
      </div>
    );

  if (!caseData)
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>No case data found</p>
        <Link to="/outsource-received" className="btn btn-secondary">
          Back
        </Link>
      </div>
    );

  // Prepare procedures data
  const procedures = caseData.selectedProcedures || [];
  let procedureList = procedures;
  if (procedureList.length === 0 && caseData.selectedProcedure) {
    procedureList = [caseData.selectedProcedure];
  }

  // Prepare files data
  const allFiles = [...originalFiles, ...additionalFiles];

  // Prepare ticket data
  const ticketData = {
    caseNumber: caseData.caseNumber || 'N/A',
    panColor: getColorName(caseData.color),
    sourceLab: sourceLabName,
    destinationLab: destinationLabName,
    status: caseData.status?.replace('_', ' ') || 'PENDING',
    createdDate: caseData.createdAt ? formatDate(caseData.createdAt) : 'N/A',
    requiredDate: caseData.requiredDate
      ? formatDate(caseData.requiredDate)
      : 'N/A',
    procedures: procedureList.map((proc, index) => ({
      unit: proc.quantity || 1,
      description: proc.procedureName || 'N/A',
      teeth: getFormattedTeeth(proc),
      shade: proc.shade || 'N/A',
      material: proc.alloyMaterial || proc.alley || 'N/A',
      fullDescription: `${index + 1} - ${proc.procedureName || 'N/A'}${
        proc.teeth ? ` - Teeth: ${getFormattedTeeth(proc)}` : ''
      }`,
      status: `Shade: ${proc.shade || 'N/A'}, Material: ${
        proc.alloyMaterial || proc.alley || 'N/A'
      }`,
    })),
    files: allFiles.map((file) => ({
      name: file.originalName || file.fileName || 'N/A',
      url: getFileUrl(file.filePath),
      uploadDate: file.uploadedBy?.uploadedAt
        ? formatDate(file.uploadedBy.uploadedAt)
        : file.createdAt
        ? formatDate(file.createdAt)
        : 'N/A',
      fileType: file.fileType || 'other',
      isStl:
        file.fileType === 'stl' ||
        (file.filePath && file.filePath.toLowerCase().endsWith('.stl')),
      isImage:
        file.fileType === 'image' ||
        (file.filePath && /\.(png|jpg|jpeg)$/i.test(file.filePath)),
      uploadedBy: file.uploadedBy?.userName || 'Unknown',
      isAdditional: additionalFiles.some((f) => f.fileId === file.fileId),
    })),
    notes: notesHistory.map((note) => ({
      content: stripHtml(note.content || note.text || 'N/A'),
      addedBy: note.addedBy?.labName || 'Unknown Lab',
      labRole: note.addedBy?.labRole || 'unknown',
      createdAt: note.createdAt ? formatDate(note.createdAt) : 'N/A',
    })),
  };

  // Calculate totals
  const totalQuantity = procedureList.reduce(
    (sum, proc) => sum + (proc.quantity || 0),
    0
  );
  const totalPrice =
    caseData.pricingSummary?.totalPrice ||
    procedureList.reduce((sum, proc) => sum + (proc.totalPrice || 0), 0);

  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        width: '100%',
        maxWidth: '100%',
        padding: '8px 12px',
        margin: '0',
        fontSize: '12px',
        color: '#000',
      }}
      className="print-work-ticket-container"
    >
      {/* Action Buttons */}
      <div className="d-flex justify-content-start mb-4 no-print">
        <Button
          variant="primary"
          style={{
            backgroundColor: '#004aad',
            borderColor: '#004aad',
            marginRight: '8px',
          }}
          onClick={handlePrint}
        >
          Print Again
        </Button>
        <Link to="/outsource-received" className="btn btn-secondary">
          Back
        </Link>
      </div>

      {/* Two-column Layout */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'flex-start',
        }}
        className="print-layout"
      >
        {/* Left Section - 58% with right border */}
        <div
          style={{
            width: '58%',
            borderRight: '1px solid #d3d3d3',
            paddingRight: '10px',
          }}
          className="left-section"
        >
          {/* Top row: Case Number & Pan Color (left) + Barcode (right) */}
          <div
            className="d-flex justify-content-between align-items-start mb-3"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'nowrap',
            }}
          >
            <div style={{ flex: '1', minWidth: '0' }}>
              <h2
                style={{
                  fontSize: '20px',
                  marginBottom: '0',
                  fontWeight: 'bolder',
                }}
              >
                OUTSOURCE CASE #{ticketData.caseNumber}
              </h2>
              <h4 style={{ fontSize: '20px', fontWeight: 'bolder' }}>
                PAN COLOR: {ticketData.panColor}
              </h4>
            </div>
            <div
              style={{
                textAlign: 'center',
                flexShrink: '0',
                marginLeft: '15px',
              }}
              className="barcode-wrapper"
            >
              <BarcodeComponent value={ticketData.caseNumber} />
            </div>
          </div>

          {/* Source & Destination Labs */}
          <div className="row mb-3">
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Source Laboratory
              </h5>
              <p className="mb-1">
                <strong>{ticketData.sourceLab}</strong>
              </p>
            </div>
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Destination Laboratory
              </h5>
              <p className="mb-1">
                <strong>{ticketData.destinationLab}</strong>
              </p>
            </div>
          </div>

          {/* Status & Created Date */}
          <div className="row mb-3">
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>Status</h5>
              <p>
                <strong>{ticketData.status}</strong>
              </p>
            </div>
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Created Date
              </h5>
              <p>
                <strong>{ticketData.createdDate}</strong>
              </p>
            </div>
          </div>

          {/* Required Date & Total Procedures */}
          <div className="row mb-3">
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Required Date
              </h5>
              <p>
                <strong>{ticketData.requiredDate}</strong>
              </p>
            </div>
            <div className="col-md-6">
              <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Total Procedures
              </h5>
              <p>
                <strong>
                  {procedureList.length} Procedures (Total Quantity:{' '}
                  {totalQuantity})
                </strong>
              </p>
            </div>
          </div>

          {/* Grand Total */}
          <div className="row mb-3">
            <div className="col-md-12">
              <h5
                style={{
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: '#004aad',
                }}
              >
                Grand Total
              </h5>
              <p
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#004aad',
                }}
              >
                {totalPrice ? `${totalPrice.toFixed(2)}` : '0.00'}
              </p>
            </div>
          </div>

          {/* Work Requested Table */}
          <div className="mb-4">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Work Requested
            </h5>
            <table
              className="table table-bordered work-requested-table"
              style={{
                marginBottom: 0,
                borderCollapse: 'collapse',
                fontSize: '11px',
                lineHeight: '1',
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: '5%',
                      padding: '1px 3px',
                      lineHeight: '12px',
                      fontSize: '11px',
                    }}
                  >
                    Unit
                  </th>
                  <th
                    style={{
                      width: '95%',
                      padding: '1px 3px',
                      lineHeight: '12px',
                      fontSize: '11px',
                    }}
                  >
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {ticketData.procedures.length > 0 ? (
                  ticketData.procedures.map((item, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          verticalAlign: 'middle',
                          padding: '1px 3px',
                          lineHeight: '12px',
                          height: '18px',
                        }}
                      >
                        {item.unit}
                      </td>
                      <td
                        className="description-cell"
                        style={{
                          padding: '1px 3px',
                          lineHeight: '12px',
                          height: '18px',
                          verticalAlign: 'middle',
                        }}
                      >
                        {item.description}
                        {item.teeth && item.teeth.length > 0 && (
                          <span
                            style={{
                              color: '#000',
                              marginLeft: '5px',
                              display: 'inline',
                            }}
                          >
                            - Teeth: {item.teeth}
                          </span>
                        )}
                        {item.shade && item.shade !== 'N/A' && (
                          <span
                            style={{
                              color: '#000',
                              marginLeft: '5px',
                              display: 'inline',
                            }}
                          >
                            - Shade: {item.shade}
                          </span>
                        )}
                        {item.material && item.material !== 'N/A' && (
                          <span
                            style={{
                              color: '#000',
                              marginLeft: '5px',
                              display: 'inline',
                            }}
                          >
                            - Material: {item.material}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" style={{ textAlign: 'center' }}>
                      No procedures requested
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Attached Files Section */}
          <div className="mb-4">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Attached Files ({ticketData.files.length})
              {originalFiles.length > 0 && additionalFiles.length > 0 && (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    color: '#6c757d',
                    marginLeft: '10px',
                  }}
                >
                  ({originalFiles.length} Original, {additionalFiles.length}{' '}
                  Additional)
                </span>
              )}
            </h5>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                padding: '10px 0',
              }}
            >
              {ticketData.files.length > 0 ? (
                ticketData.files.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      width: '120px',
                    }}
                    className="file-preview"
                  >
                    <FilePreview file={file} />
                    <div
                      style={{
                        fontSize: '9px',
                        marginTop: '5px',
                        wordBreak: 'break-all',
                        maxWidth: '100px',
                        color: '#333',
                      }}
                    >
                      {file.name.length > 15
                        ? file.name.substring(0, 15) + '...'
                        : file.name}
                    </div>
                    {file.isAdditional && (
                      <div
                        style={{
                          fontSize: '7px',
                          color: '#004aad',
                          fontWeight: 'bold',
                          marginTop: '2px',
                        }}
                      >
                        Additional
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p>No attached files</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - 42% with notes */}
        <div
          style={{
            width: '42%',
            paddingLeft: '20px',
          }}
          className="right-section"
        >
          {/* Source Lab */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Source Laboratory
            </h5>
            <p>
              <strong>{ticketData.sourceLab}</strong>
            </p>
          </div>

          {/* Destination Lab */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Destination Laboratory
            </h5>
            <p>
              <strong>{ticketData.destinationLab}</strong>
            </p>
          </div>

          {/* Status */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>Status</h5>
            <p>
              <strong>{ticketData.status}</strong>
            </p>
          </div>

          {/* Required Date */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Required Date
            </h5>
            <p>
              <strong>{ticketData.requiredDate}</strong>
            </p>
          </div>

          {/* Total Amount */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Total Amount
            </h5>
            <p
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#004aad',
              }}
            >
              {totalPrice ? `${totalPrice.toFixed(2)}` : '0.00'}
            </p>
          </div>

          {/* Notes */}
          <div className="mb-3">
            <h5 style={{ fontWeight: 'bold', fontSize: '16px' }}>
              Notes ({ticketData.notes.length})
            </h5>
            {loadingNotes ? (
              <p>Loading notes...</p>
            ) : ticketData.notes.length > 0 ? (
              ticketData.notes.map((note, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '10px',
                    padding: '10px',
                    backgroundColor:
                      note.labRole === 'destination' ? '#f0f8ff' : '#f8f9fa',
                    borderRadius: '4px',
                    border: `1px solid ${
                      note.labRole === 'destination' ? '#b6d4fe' : '#dee2e6'
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '5px',
                      flexWrap: 'wrap',
                      gap: '4px',
                    }}
                  >
                    <strong style={{ color: '#004aad', fontSize: '11px' }}>
                      {note.addedBy}
                    </strong>
                    <span
                      style={{
                        fontSize: '9px',
                        padding: '1px 6px',
                        borderRadius: '10px',
                        backgroundColor:
                          note.labRole === 'destination'
                            ? '#d4edda'
                            : '#fff3cd',
                        color:
                          note.labRole === 'destination'
                            ? '#155724'
                            : '#856404',
                        fontWeight: '500',
                      }}
                    >
                      {note.labRole === 'destination'
                        ? 'Destination Lab'
                        : 'Source Lab'}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#333',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: '1.4',
                    }}
                    dangerouslySetInnerHTML={{ __html: note.content }}
                  />
                  <div
                    style={{
                      fontSize: '9px',
                      color: '#999',
                      marginTop: '4px',
                      textAlign: 'right',
                    }}
                  >
                    {note.createdAt}
                  </div>
                </div>
              ))
            ) : (
              <p>No notes available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutsourcePrintWorkTicket;